import { cn } from '@eriko/ui/src/lib/utils';
import { Brain, CreditCard, Link2, ListFilter, Receipt, SlidersHorizontal, User, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountingTab } from './components/AccountingTab';
import { BillingTab } from './components/BillingTab';
import { MemoryTab } from './components/MemoryTab';
import { PreferencesTab } from './components/PreferencesTab';
import { ProfileTab } from './components/ProfileTab';
import { RulesTab } from './components/RulesTab';
import { TaxTab } from './components/TaxTab';
import { TeamTab } from './components/TeamTab';

const TAB_IDS = ['profile', 'team', 'billing', 'preferences', 'accounting', 'memory', 'rules', 'tax'] as const;
const TAB_ICONS = {
  profile: User,
  team: Users,
  billing: CreditCard,
  preferences: SlidersHorizontal,
  accounting: Link2,
  memory: Brain,
  rules: ListFilter,
  tax: Receipt,
} as const;

type TabId = (typeof TAB_IDS)[number];

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  const tabLabel = (id: TabId): string => {
    if (id === 'memory') return 'Memory';
    if (id === 'rules') return 'Rules';
    if (id === 'billing') return 'Billing';
    if (id === 'tax') return 'Tax';
    return t(`tabs.${id}`);
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-6 w-full">
      <h1 className="text-2xl font-bold text-surface-900 tracking-tight mb-6">{t('title')}</h1>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-8 border-b border-surface-200 w-full">
        {TAB_IDS.map((tabId) => {
          const isActive = activeTab === tabId;
          const Icon = TAB_ICONS[tabId];

          return (
            <button
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-neg text-surface-900 font-semibold'
                  : 'border-transparent text-surface-500 font-medium hover:text-surface-900 hover:border-surface-300',
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} />
              {tabLabel(tabId)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'team' && <TeamTab />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'preferences' && <PreferencesTab />}
      {activeTab === 'accounting' && <AccountingTab />}
      {activeTab === 'memory' && <MemoryTab />}
      {activeTab === 'rules' && <RulesTab />}
      {activeTab === 'tax' && <TaxTab />}
    </div>
  );
}
